import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { useDraggableColumn } from '../hooks/useDraggableColumn';
import { useDroppableColumn } from '../hooks/useDroppableColumn';
import { useCardHeights } from '../hooks/useCardHeights';
import { useBoardActions, useColumnInfiniteData, useBoardData, useBoardState } from '../context/BoardContext';
import { useLeadActions } from '../context/LeadActionsContext';
import ColumnHeader from './BoardColumn/ColumnHeader';
import PracticeAreaTags from './BoardColumn/PracticeAreaTags';
import ColumnContent from './BoardColumn/ColumnContent';

const BoardColumn = ({ column, index, filters = {} }) => {
  const { renameColumn, archiveColumn, moveCard, reorderColumns } = useBoardActions();
  const { openCloseLead } = useLeadActions();
  const { isRefetchingData } = useBoardState();

  // Use infinite query for this column - automatic pagination management with filters
  const { items, totalCount, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useColumnInfiniteData(column.sk, filters);

  const { practiceAreaList } = useBoardData();

  const prevItemsRef = useRef([]);
  const { listRef, getCardHeight, setCardHeight, resetList } = useCardHeights(column.sk);

  const { containerRef, handleRef, isDragging, isLocked } = useDraggableColumn({
    columnId: column.sk,
    index,
    isLocked: column.isLocked,
  });

  // Intercept card drops to Closed stage and open dialog
  const handleCardDrop = useCallback(
    (dropData) => {
      const { sourceColumnId, targetColumnId, sourceCardId, targetCardId, edge, leadItem } = dropData;

      // Check if dropping in the same column
      const isSameColumn = sourceColumnId === targetColumnId;

      // Check if dropping in the same position (same column, target is self or adjacent)
      if (isSameColumn) {
        // Find source and target indices in current items
        const sourceIndex = items.findIndex((item) => item.sk === sourceCardId);
        const targetIndex = items.findIndex((item) => item.sk === targetCardId);

        if (sourceIndex !== -1 && targetIndex !== -1) {
          // Calculate where the card would be inserted
          let insertPosition = targetIndex;
          if (edge === 'bottom') {
            insertPosition = targetIndex + 1;
          }

          // Check if dropping in the same position or adjacent position
          // Same position: insertPosition === sourceIndex or insertPosition === sourceIndex + 1
          if (insertPosition === sourceIndex || insertPosition === sourceIndex + 1) {
            console.log('Card dropped in same position, skipping API call');
            return; // Same position - do nothing
          }
        }
      }

      // Check if dropping to Closed stage from a different stage
      const isClosedStage = column.lead_stage_name === 'Closed';
      const isDifferentStage = sourceColumnId !== targetColumnId;

      if (isClosedStage && isDifferentStage && leadItem) {
        // Only intercept if lead is open
        if (leadItem.is_open) {
          openCloseLead(leadItem);
          return; // Intercept - don't call moveCard
        }
      }

      // Normal drag behavior for other stages or closed leads
      moveCard(dropData);
    },
    [column.lead_stage_name, openCloseLead, moveCard, items]
  );

  const { isDraggingOver, edge, type } = useDroppableColumn({
    containerRef,
    columnId: column.sk,
    index,
    onCardDrop: handleCardDrop,
    onColumnReorder: reorderColumns,
    isLocked: column.isLocked,
  });

  // Reset list when tasks are reordered
  useEffect(() => {
    const prevItems = prevItemsRef.current;
    const itemsChanged = items.length !== prevItems.length || items.some((item, idx) => item.sk !== prevItems[idx]?.sk);

    if (itemsChanged && prevItems.length > 0) {
      resetList();
    }

    prevItemsRef.current = items;
  }, [items, resetList]);

  // Memoize derived values
  const showColumnEdge = useMemo(() => type === 'column' && edge, [type, edge]);
  const showCardHighlight = useMemo(() => type === 'card' && isDraggingOver, [type, isDraggingOver]);
  const isDefaultColumn = column?.is_default;

  return (
    <div
      ref={containerRef}
      className={`board-column ${isDragging ? 'column-dragging' : ''}`}
      style={{
        opacity: isDragging ? 0.85 : 1,
        position: 'relative',
      }}
    >
      {/* Drop indicators for column reordering */}
      {showColumnEdge && edge === 'left' && <div className="column-drop-indicator left" />}
      {showColumnEdge && edge === 'right' && <div className="column-drop-indicator right" />}

      {/* Column Header */}
      <ColumnHeader
        column={column}
        itemsCount={items.length}
        totalItems={totalCount}
        isLocked={isLocked}
        handleRef={handleRef}
        isDefaultColumn={isDefaultColumn}
        onRename={renameColumn}
        onArchive={archiveColumn}
      />

      {/* Practice Area Tags */}
      <div className="d-flex align-items-center mt-2 px-3">
        <div className="col-12">
          <PracticeAreaTags column={column} practiceAreaList={practiceAreaList} />
        </div>
      </div>

      {/* Column Content with Infinite Scroll */}
      <ColumnContent
        columnId={column.sk}
        columnStageName={column.lead_stage_name}
        items={items}
        isLoading={isLoading}
        isRefetchingData={isRefetchingData}
        hasMore={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        onLoadMore={fetchNextPage}
        onCardDrop={handleCardDrop}
        getCardHeight={getCardHeight}
        setCardHeight={setCardHeight}
        listRef={listRef}
        showCardHighlight={showCardHighlight}
      />
    </div>
  );
};

// Memoize with custom comparison function
// Note: Return TRUE if props are equal (prevent rerender), FALSE if different (allow rerender)
export default React.memo(BoardColumn, (prevProps, nextProps) => {
  // Check if assignedTo arrays are equal (avoid JSON.stringify for performance)
  const assignedToEqual =
    prevProps.filters.assignedTo?.length === nextProps.filters.assignedTo?.length &&
    prevProps.filters.assignedTo?.every((id, i) => id === nextProps.filters.assignedTo?.[i]);

  const filtersEqual =
    prevProps.filters.searchKeyword === nextProps.filters.searchKeyword &&
    prevProps.filters.practiceArea === nextProps.filters.practiceArea &&
    prevProps.filters.sortType === nextProps.filters.sortType &&
    assignedToEqual;

  // Return TRUE if all props are equal (prevent rerender)
  return (
    prevProps.column.sk === nextProps.column.sk &&
    prevProps.index === nextProps.index &&
    prevProps.column.lead_stage_name === nextProps.column.lead_stage_name &&
    prevProps.column.isLocked === nextProps.column.isLocked &&
    filtersEqual
  );
});
